'use client';

import { useEffect } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useGetUserInfo } from '@/features/auth/services/userinfo';
import { userInfoAtom } from '@/store/userinfo';
import { useGetBaseInfo } from "@/features/home/services/base";
import { setBaseInfoAtom, setBaseInfoLoadingAtom } from "@/store/base";
import type { BaseinfoRes } from '@/types/api/hall/baseinfo';
import { userAtom } from '@/store';
import { balanceListAtom } from '@/store/balance';
import { refreshCommonProperties, setDistinctId } from '@/utils/track';
import { usePrefetchRummyGameUrl } from '@/app/[locale]/(main)/(non-activity)/rummy/hooks/use-prefetch-url';
import { toNum } from '@/libs/decimal';
import { useRouter } from '@/i18n/navigation';
import { queryString } from '@/utils/tools';
import { utmActAtom } from '@/store/attribute-uuid';
import { hiddenInviteCodeQueryAtom } from '@/store/invite-code';
import { useAfidBind } from '@/features/auth/services/afid-bind';
import { getAFID, getDeviceInfo, getFcmToken, isInApp, isInVest } from '@/libs/comeAppSDK';
import Cookies from 'js-cookie';
import { getUtmActFromStorage } from '@/utils/browser';
import { setSentryUser } from '@/utils/sentry-utils';
import { useFcmTokenBind } from '../../services/fcm-token-bind';

export default function UserinfoEffect() {
  const user = useAtomValue(userAtom);
  const router = useRouter();
  const [, setUserInfoData] = useAtom(userInfoAtom);
  const setBaseInfoData = useSetAtom(setBaseInfoAtom);
  const setBaseInfoLoading = useSetAtom(setBaseInfoLoadingAtom);
  const balanceList = useAtomValue(balanceListAtom);
  const utmAct = useAtomValue(utmActAtom);
  const setHiddenInviteCodeQuery = useSetAtom(hiddenInviteCodeQueryAtom);

  const { data: userInfo, refetch: refetchUserInfo } = useGetUserInfo.useQueryHook(
    {
      params: {},
      queryConfig: {
        enabled: false,
      },
    }
  );

  const utmActFromUrl = queryString<{ utm_act?: string; }>()?.utm_act;
  const utmActValue = utmActFromUrl || utmAct || getUtmActFromStorage();
  const { data: baseInfo, refetch: refetchBaseInfo } = useGetBaseInfo.useQueryHook({
    params: {
      utm_act: utmActValue,
    },
    queryConfig: {
      enabled: false,
    },
  });
  const prefetchRummyGameUrl = usePrefetchRummyGameUrl({
    delay: 800,
    is_preview: true,
  });

  // 将pwa落地页带过来的隐藏邀请码的参数存起来
  const hiddenInviteCodeQuery = queryString<{ hiddenInviteCode?: '1' | ''; }>()?.hiddenInviteCode || '';
  setHiddenInviteCodeQuery(hiddenInviteCodeQuery);

  // 登陆后获取
  useEffect(() => {
    if (user?.accessToken) {
      refetchUserInfo();
    }
    refetchBaseInfo();
  }, [user?.accessToken, refetchUserInfo, refetchBaseInfo]);

  // 监听 utmActAtom 的变化，当从 localStorage 加载到值后重新请求
  useEffect(() => {
    if (utmAct) {
      refetchBaseInfo();
    }
  }, [utmAct, refetchBaseInfo]);

  // 归因相关的上报
  const { mutate: afidBind } = useAfidBind();
  const { mutate: fcmTokenBind } = useFcmTokenBind();
  const afidBindData = async () => {
    let afid = '';
    let appId = '';
    if (isInApp()) {
      afid = await getAFID() || '';
      const deviceInfo = await getDeviceInfo() || '';
      appId = deviceInfo?.appId || '';
      if (isInVest()) {
        let fcmToken = '';
        try {
          fcmToken = (await getFcmToken()) || '';
        } catch (err) {
          // FCM token 获取失败不应影响后续流程
          console.warn('[userinfo-effect] getFcmToken failed:', err);
        }

        // 绑定 fcm token（拿不到 token 直接跳过）
        if (fcmToken) {
          fcmTokenBind({
            params: {
              //【可选】 设备类型：1 web, 2 安卓，3 ios
              fcmDeviceType: 2,
              fcmToken,
              appId,
            }
          });
        }
      }
    } else {
      // afUserId 从cookie中获取
      afid = Cookies.get('afUserId') || '';
    }
    if (afid) {
      if (appId) {
        afidBind({
          params: {
            afid: afid,
            appId: appId,
            deviceType: isInApp() ? 2 : 1,
          },
        });
      } else {
        afidBind({
          params: {
            afid: afid,
            deviceType: isInApp() ? 2 : 1,
          },
        });
      }
    }
  };
  useEffect(() => {
    if (userInfo?.id) {
      afidBindData();
    }
  }, [userInfo, afidBind]);
  useEffect(() => {
    if (baseInfo) {
      setBaseInfoData(baseInfo);
      setBaseInfoLoading(false);
      // 提前获取rummyUrl
      // 在游戏界面刷新不发请求，避免重复请求
      const path = location.pathname;
      if (!path.includes('games')) {
        prefetchRummyGameUrl();
      }
    } else {
      setBaseInfoData({} as BaseinfoRes);
    }
  }, [baseInfo, setBaseInfoData, prefetchRummyGameUrl]);

  useEffect(() => {
    if (userInfo) {
      setUserInfoData(userInfo);
      setDistinctId(userInfo?.id?.toString());
      // Set Sentry user context when user logs in
      setSentryUser(userInfo.userId, userInfo.userName);
      // 持久化 userId 到 localStorage，供 Sentry 在下次 session 启动时早期读取
      // key 使用 last_uid，与业务侧的 accessToken/deviceId 区分，仅作辅助追踪用途
      try {
        if (userInfo.userId) {
          localStorage.setItem('last_uid', String(userInfo.userId));
        }
      } catch { /* ignore */ }
    }
  }, [userInfo, setUserInfoData]);

  // 监听 balanceListAtom 的变化进行打点
  useEffect(() => {
    if (balanceList) {
      refreshCommonProperties({
        current_vip_level: toNum(balanceList?.vip_level),
        current_deposit_amount: toNum(balanceList?.deposit_balance),
        current_bonus_amount: toNum(balanceList?.bonus_balance),
        current_withdraw_amount: toNum(balanceList?.withdraw_balance),
      });
    }
  }, [balanceList]);

  const setUtmAct = useSetAtom(utmActAtom);
  // 归因参数
  useEffect(() => {
    const utmAct = queryString<{ utm_act?: string; }>()?.utm_act || '';
    if (utmAct) {
      setUtmAct(utmAct);
    }
  }, [setUtmAct]);

  // 监听游戏中页面发送的消息，证明游戏异常了，返回首页
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      // 处理消息数据
      const messageData = event.data;

      // 验证消息来源
      if (messageData.from !== 'comescheme') {
        return;
      }

      switch (messageData.type) {
        case 'come_game_redirect':
          // ✅ 修复：支持自定义目标路径，默认跳转到首页
          // 使用 locale-aware router，自动添加 locale 前缀
          const targetPath = messageData.targetPath || '/';
          router.push(targetPath);

          // 添加日志方便调试
          if (process.env.NODE_ENV === 'development') {
            console.warn('Game redirect triggered:', messageData);
          }
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [router]); // ✅ 添加 router 到依赖数组

  return null;
};